import React, { useEffect, useState } from 'react';
import { paymentService } from '../../_services';
import toast from '../shared/Toast';
import { loadStripe } from '@stripe/stripe-js';
import siteSetting from '../../config/env';
import SlidePanel from '../shared/SlidePanel';

/**
 * The Billing page's saved cards, shared by Settings › Billing and the
 * pricing page (Plan & billing): the card helpers, the hook that loads and
 * changes them, and the card detail panel. Moved here unchanged from
 * Billing.js so both pages describe a card the same way.
 */
const stripePromise = loadStripe(siteSetting.stripeKey);

/* ── Helpers ── */

/* A payment method is only a CARD when Stripe attaches a .card object.
   Link (and any future type) has none, so card-only fields - brand, last4,
   expiry - must not render for it. Shared by the list rows and the detail
   panel so both describe a method the same way. */
export function methodBrand(pm) {
    // Stripe's API type is still 'link', but the product rebranded to Onelink.
    return pm?.card?.brand || (pm?.type === 'link' ? 'Onelink' : (pm?.type || 'Saved method'));
}

export function isCardExpired(card) {
    const now = new Date();
    const expMonth = card?.card?.exp_month;
    const expYear = card?.card?.exp_year;
    if (expYear < now.getFullYear()) return true;
    if (expYear === now.getFullYear() && expMonth < now.getMonth() + 1) return true;
    return false;
}

export function brandIcon(brand) {
    const b = (brand || '').toLowerCase();
    if (b === 'visa') return (
        <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect width="32" height="20" rx="3" fill="#1A1F71"/><path d="M13.2 13.5l1.4-8.5h2.2l-1.4 8.5h-2.2zm8.8-8.3c-.4-.2-1.1-.3-2-.3-2.2 0-3.7 1.1-3.7 2.7 0 1.2 1.1 1.8 2 2.2.9.4 1.2.7 1.2 1 0 .6-.7.8-1.4.8-1 0-1.5-.1-2.3-.5l-.3-.2-.3 2c.6.3 1.6.5 2.7.5 2.3 0 3.8-1.1 3.8-2.8 0-.9-.6-1.6-1.9-2.2-.8-.4-1.3-.6-1.3-1 0-.3.4-.7 1.3-.7.7 0 1.3.1 1.7.3l.2.1.3-1.9zm5.5.2h-1.7c-.5 0-.9.1-1.1.7l-3.2 7.4h2.3l.5-1.2h2.8l.3 1.2H29l-1.5-8.1zm-2.7 5.2l1.2-3.1.6 3.1h-1.8zM12 5l-2 5.8-.2-1.1-.8-3.8c-.1-.5-.5-.7-1-.7H5l0 .2c.8.2 1.7.5 2.2.8l1.9 7h2.3L14.4 5H12z" fill="#fff"/></svg>
    );
    if (b === 'mastercard') return (
        <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect width="32" height="20" rx="3" fill="#252525"/><circle cx="12.5" cy="10" r="6" fill="#EB001B"/><circle cx="19.5" cy="10" r="6" fill="#F79E1B"/><path d="M16 5.3a6 6 0 010 9.4 6 6 0 000-9.4z" fill="#FF5F00"/></svg>
    );
    if (b === 'amex' || b === 'american express') return (
        <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect width="32" height="20" rx="3" fill="#2E77BC"/><text x="16" y="12" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="700" fontFamily="sans-serif">AMEX</text></svg>
    );
    // Generic card
    return (
        <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect width="32" height="20" rx="3" fill="#E0E0E0"/><rect x="4" y="5" width="8" height="5" rx="1" fill="#BDBDBD"/><rect x="4" y="13" width="14" height="2" rx="1" fill="#BDBDBD"/></svg>
    );
}


export function useBillingCards() {
    const [cardList, setCardList] = useState([]);
    const [defaultPayment, setDefaultPayment] = useState();
    const [selectedCard, setSelectedCard] = useState(null);

    useEffect(() => {
        fetchCards();
    }, []);

    /* ── API ── */

    const fetchCards = () => {
        paymentService.checkCardAdded({}).then((res) => {
            if (res.data.statusCode === 1) {
                const data = res.data.responseData;
                const pmId = data?.stripeCustomer?.invoice_settings?.default_payment_method;
                if (pmId) setDefaultPayment(pmId);
                if (data?.result?.data?.length > 0) {
                    setCardList(data.result.data);
                }
            }
        });
    };

    const addCard = async () => {
        const stripe = await stripePromise;
        paymentService.paymentMethodAdd({}).then((res) => {
            if (res.data.statusCode === 1) {
                const sessionId = res.data.responseData?.result?.id;
                if (sessionId) {
                    stripe.redirectToCheckout({ sessionId })
                        .catch((err) => toast.error(err.message));
                } else {
                    toast.error('Payment gateway error');
                }
            }
        });
    };

    const removeCard = () => {
        if (!selectedCard?.id) return;
        paymentService.cardRemove({ stripePaymentMethodId: selectedCard.id })
            .then((res) => {
                if (res.data.statusCode === 1) {
                    toast.success('Card removed');
                    setSelectedCard(null);
                    fetchCards();
                }
            });
    };

    const makeDefault = () => {
        if (!selectedCard?.id) return;
        paymentService.cardMakePrimery({ stripePaymentMethodId: selectedCard.id })
            .then((res) => {
                if (res.data.statusCode === 1) {
                    toast.success('Default card updated');
                    setSelectedCard(null);
                    fetchCards();
                }
            });
    };

    /* Auto-set single card as default */
    useEffect(() => {
        if (cardList.length === 1 && defaultPayment !== cardList[0].id) {
            setSelectedCard(cardList[0]);
            paymentService.cardMakePrimery({ stripePaymentMethodId: cardList[0].id })
                .then((res) => {
                    if (res.data.statusCode === 1) fetchCards();
                });
        }
        /* eslint-disable-next-line */
    }, [cardList]);


    return { cardList, defaultPayment, selectedCard, setSelectedCard, fetchCards, addCard, removeCard, makeDefault };
}

/** The card detail panel (remove / make default), as on Billing. */
export function CardDetailPanel({ cards }) {
    const { cardList, defaultPayment, selectedCard, setSelectedCard, removeCard, makeDefault } = cards;
    return (
        <>
            {selectedCard && (
                <SlidePanel
                    title={selectedCard?.card ? 'Card Details' : 'Payment Method'}
                    onClose={() => setSelectedCard(null)}
                    width={420}
                    footer={
                        cardList.length > 1 ? (
                            <div className="bi-panel-footer">
                                <button className="settings-btn settings-btn-secondary bi-panel-btn" onClick={removeCard}>
                                    {selectedCard?.card ? 'Remove card' : 'Remove method'}
                                </button>
                                {defaultPayment !== selectedCard.id && (
                                    <button className="settings-btn settings-btn-primary bi-panel-btn" onClick={makeDefault}>
                                        Make default
                                    </button>
                                )}
                            </div>
                        ) : null
                    }
                >
                    <div className="bi-detail">
                        <div className={`bi-detail-card-visual${selectedCard?.type === 'link' ? ' bi-detail-card-visual--onelink' : ''}`}>
                            <div className="bi-detail-brand-row">
                                {brandIcon(selectedCard?.card?.brand)}
                                {defaultPayment === selectedCard.id && (
                                    <span className="bi-badge bi-badge--default">Default</span>
                                )}
                                {isCardExpired(selectedCard) && (
                                    <span className="bi-badge bi-badge--expired">Expired</span>
                                )}
                            </div>
                            {/* Card-only visuals (masked number, expiry) render only when
                                there IS a card. A Link method shows its identity instead -
                                the old unconditional render produced a blank number and the
                                literal text "undefined /" as the expiry. */}
                            {selectedCard?.card ? (
                                <>
                                    <div className="bi-detail-number">
                                        <span>••••</span>
                                        <span>••••</span>
                                        <span>••••</span>
                                        <span>{selectedCard.card.last4}</span>
                                    </div>
                                    <div className="bi-detail-bottom">
                                        <div>
                                            <span className="bi-detail-label">Cardholder</span>
                                            <span className="bi-detail-value">{selectedCard?.billing_details?.name || 'Card holder'}</span>
                                        </div>
                                        <div>
                                            <span className="bi-detail-label">Expires</span>
                                            <span className="bi-detail-value">
                                                {String(selectedCard.card.exp_month).padStart(2, '0')} / {selectedCard.card.exp_year}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="bi-detail-number">
                                        <span>{methodBrand(selectedCard)}</span>
                                    </div>
                                    <div className="bi-detail-bottom">
                                        <div>
                                            <span className="bi-detail-label">
                                                {selectedCard?.type === 'link' ? 'Onelink account' : 'Name'}
                                            </span>
                                            <span className={`bi-detail-value${selectedCard?.link?.email ? ' bi-email' : ''}`}>
                                                {selectedCard?.link?.email
                                                    || selectedCard?.billing_details?.name
                                                    || 'Saved payment method'}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="bi-detail-rows">
                            <div className="bi-detail-row">
                                <span>{selectedCard?.card ? 'Brand' : 'Type'}</span>
                                <span>{methodBrand(selectedCard)}</span>
                            </div>
                            {selectedCard?.card ? (
                                <>
                                    <div className="bi-detail-row">
                                        <span>Last 4 digits</span>
                                        <span>{selectedCard.card.last4}</span>
                                    </div>
                                    <div className="bi-detail-row">
                                        <span>Expiry</span>
                                        <span>{String(selectedCard.card.exp_month).padStart(2, '0')} / {selectedCard.card.exp_year}</span>
                                    </div>
                                </>
                            ) : (
                                selectedCard?.link?.email && (
                                    <div className="bi-detail-row">
                                        <span>Email</span>
                                        <span className="bi-email">{selectedCard.link.email}</span>
                                    </div>
                                )
                            )}
                            <div className="bi-detail-row">
                                <span>Status</span>
                                <span>{isCardExpired(selectedCard) ? 'Expired' : 'Active'}</span>
                            </div>
                        </div>
                    </div>
                </SlidePanel>
            )}
        </>
    );
}
